/* eslint-disable max-lines-per-function */
import "data/yup-validation";
import { Formik, FormikConfig } from "formik";
import React from "react";
import { Grid, Typography } from "@mui/material";
import * as y from "yup";
import { HistoryBackButton } from "components/buttons";
import { Checkbox, Form, SubmitButton } from "components/haven-design/form";
import {
  InputWithErrorLabel as Input,
  MaskedInputWithErrorLabel,
} from "components/haven-design/form/inputs";
import {
  SelectInterestList,
  SelectOrganization,
} from "components/haven-design/form/special-fields";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { extendedStatesList } from "components/haven-design/state-picker";
import { PropertyCreateInput } from "types/gql-types";
import { US_PHONE_MASK } from "utils/phone";
import { nullableString } from "utils/yup-helpers";
import { AdminFormLayout } from "../shared/admin-form";

export type Property = PropertyCreateInput;

type Props = {
  initialData: Property;
  formType: "update" | "new";
  handleSubmit: FormikConfig<Property>["onSubmit"];
  sending: boolean;
}

// https://github.com/jquense/yup
export const formSchema = y.object().shape({
  __typename: y.string().strip(true),

  id: y.string().strip(true),

  organizationId: y
    .string()
    .trim()
    .required(),

  name: y
    .string()
    .trim()
    .max(40)
    .required(),

  phone: nullableString()
    .validPhoneNumber("Invalid phone number")
    .required(),

  phoneExt: nullableString(),

  email: nullableString()
    .email("Incorrect email format")
    .lowercase()
    .required(),

  interestListId: y
    .string()
    .trim()
    .required(),

  address: y.object().shape({
    __typename: y.string().strip(true),

    address1: nullableString().required(),

    address2: nullableString(),

    city: nullableString().required(),

    state: nullableString().required(),

    zip: nullableString()
      .matches(/^\d{5}(?:[-\s]\d{4})?$/, "Incorrect zipcode format")
      .required(),
  }),

  submissionDigestEnabled: y.boolean(),

  units: y
    .mixed()
    .nullable()
    .transform((val) => {
      // eslint-disable-next-line no-undefined
      if (["", null, undefined].includes(val)) return null;
      const num = parseInt(String(val), 10);
      return isNaN(num) ? val : num;
    })
    .test("is-int", "Must be a whole number", (val) =>
      val === null || (typeof val === "number" && Number.isInteger(val) && val >= 0)),

  invoicedCustomer: y.object().shape({
    __typename: y.string().strip(true),
  }),
});

const PropertiesForm: React.FC<Props> = (props: Props) => (
  <AdminFormLayout>
    <Formik
      initialValues={props.initialData}
      onSubmit={props.handleSubmit}
      validationSchema={formSchema}>
      {({ isSubmitting, values }) => (
        <Form>
          <Grid container spacing={2} className="card" sx={{ mb: "30px" }}>
            <Typography>Required fields marked with *</Typography>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <SelectOrganization name="organizationId" label="Organization *" showLabel />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <SelectInterestList
                  label="Associated Interest List *"
                  name="interestListId"
                  isWaitlist
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Property Name *" name="name" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <MaskedInputWithErrorLabel
                  margin="none"
                  label="Property Contact Phone *"
                  name="phone"
                  mask={US_PHONE_MASK}
                  guide={false}
                />
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Phone Extension" name="phoneExt" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Property Contact Email *" name="email" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Checkbox
                  label="Receive Daily Submission Email Digest"
                  name="submissionDigestEnabled"
                  checked={!!values.submissionDigestEnabled}
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Address *" name="address.address1" type="text" />
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Address 2" name="address.address2" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="City *" name="address.city" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <MaterialFormikSelectAutocomplete
                  margin="none"
                  id="address.state"
                  name="address.state"
                  placeholder="State *"
                  title="State *"
                  options={extendedStatesList}
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Zip *" name="address.zip" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Checkbox
                  label="Enable Invoiced Billing"
                  name="invoicedCustomer.active"
                  checked={!!values.invoicedCustomer?.active}
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Invoiced Customer Number" name="invoicedCustomer.invoicedId" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input
                  margin="none"
                  label="Units"
                  name="units"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <HistoryBackButton marginRight size="L" />

            <SubmitButton isWide text="Submit" loadingText="Sending" isLoading={isSubmitting} />
          </Grid>
        </Form>
      )}
    </Formik>
  </AdminFormLayout>
);

export default PropertiesForm;
